import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  SelectAll as SelectAllIcon,
} from '@mui/icons-material';
import { CreateProductInput } from '@/services/product-catalogue/pc-api';
import ProductReviewCard from './components/product-review-card';
import { validateProduct } from './components/validation-alerts';

interface BatchProductReviewProps {
  products: CreateProductInput[];
  onUpdate: (updatedProducts: CreateProductInput[]) => void;
  selectedProducts: boolean[];
  onSelectedProductsChange: (selected: boolean[]) => void;
}

const BatchProductReview: React.FC<BatchProductReviewProps> = ({
  products,
  onUpdate,
  selectedProducts,
  onSelectedProductsChange,
}) => {
  // selectedProducts now managed by parent component
  // Batch creation logic moved to parent component

  // Validation statistics
  const validationStats = useMemo(() => {
    let validCount = 0;
    let errorCount = 0;
    let warningCount = 0;

    products.forEach(product => {
      const issues = validateProduct(product);
      const hasErrors = issues.some(issue => issue.type === 'error');
      const hasWarnings = issues.some(issue => issue.type === 'warning');

      if (hasErrors) {
        errorCount++;
      } else if (hasWarnings) {
        warningCount++;
      } else {
        validCount++;
      }
    });

    return { validCount, errorCount, warningCount };
  }, [products]);

  const selectedCount = selectedProducts.filter(Boolean).length;

  const handleProductUpdate = (index: number, updatedProduct: CreateProductInput) => {
    const newProducts = [...products];
    newProducts[index] = updatedProduct;
    onUpdate(newProducts);
  };

  const handleProductRemove = (index: number) => {
    const newProducts = products.filter((_, i) => i !== index);
    const newSelected = selectedProducts.filter((_, i) => i !== index);
    onUpdate(newProducts);
    onSelectedProductsChange(newSelected);
  };

  const handleSelectAll = () => {
    const allSelected = selectedProducts.every(Boolean);
    onSelectedProductsChange(new Array(products.length).fill(!allSelected));
  };


  // Submit logic moved to parent component

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Review Products
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Review and edit your products before uploading. Fix any errors and warnings.
        </Typography>

        {/* Statistics */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={3}>
            <Chip
              icon={<CheckCircleIcon />}
              label={`${validationStats.validCount} Valid`}
              color="success"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip
              icon={<WarningIcon />}
              label={`${validationStats.warningCount} Warnings`}
              color="warning"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip
              icon={<ErrorIcon />}
              label={`${validationStats.errorCount} Errors`}
              color="error"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip
              label={`${selectedCount}/${products.length} Selected`}
              color="primary"
              variant="outlined"
            />
          </Grid>
        </Grid>

        {/* Batch Actions */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedProducts.every(Boolean)}
                indeterminate={selectedProducts.some(Boolean) && !selectedProducts.every(Boolean)}
                onChange={handleSelectAll}
              />
            }
            label="Select All"
          />
          
          <Button
            variant="outlined"
            startIcon={<SelectAllIcon />}
            onClick={handleSelectAll}
            size="small"
          >
            {selectedProducts.every(Boolean) ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        {validationStats.errorCount > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Please fix all errors before uploading. Products with errors cannot be created.
          </Alert>
        )}
      </Paper>

      {/* Product List */}
      <Box sx={{ mb: 3 }}>
        {products.map((product, index) => (
          <ProductReviewCard
            key={index}
            product={product}
            index={index}
            onUpdate={handleProductUpdate}
            onRemove={handleProductRemove}
            isSelected={selectedProducts[index]}
            requiresMetalPercentage={false} // TODO: Implement metal percentage checking
          />
        ))}
      </Box>

      {/* Actions removed - handled by parent component */}
    </Box>
  );
};

export default BatchProductReview;